import { useRouter, type ReactNode } from '@tanstack/react-router';

type TableHeadProps = {
	headers: string[];
};

type AdvancedTableHeadProps = {
	headers: { name: string; key?: string; hideOnMobile?: boolean }[];
};

type TableProps = TableHeadProps & {
	className?: string;
};

type AdvancedTableProps = AdvancedTableHeadProps & {
	className?: string;
};

type RowProps = {
	rows: ReactNode[][];
};

type LinkRowProps = {
	rows: { href: string; data: (string | ReactNode)[] }[];
};

function TableRows({ rows }: RowProps) {
	return (
		<>
			{rows.map((row, index) => (
				<tr key={index} className={`${index % 2 == 0 ? '' : 'bg-gray-100'}`}>
					{row.map((cell, i) => (
						<td key={i} className={'border-gray-200 p-3'}>
							{cell}
						</td>
					))}
				</tr>
			))}
		</>
	);
}

function AdvancedTableRows({ rows, headers }: RowProps & AdvancedTableHeadProps) {
	return (
		<>
			{rows.map((row, index) => (
				<tr key={index} className={`${index % 2 == 0 ? '' : 'bg-gray-100'}`}>
					{row.map((cell, i) => {
						const header = headers[i];
						return (
							<td key={i} className={`border-gray-200 p-3 ${header?.hideOnMobile && 'hidden sm:table-cell'}`}>
								{cell}
							</td>
						);
					})}
				</tr>
			))}
		</>
	);
}

function LinkTableRows({ rows }: LinkRowProps) {
	const router = useRouter();
	return (
		<>
			{rows.map((row, index) => (
				// use Link/to instead of href
				<tr key={index} onClick={() => router.navigate({ to: row.href })} className="cursor-pointer hover:bg-gray-100">
					{row.data.map((cell, i) => (
						<td key={i} className={'border-gray-200 p-3'}>
							{cell}
						</td>
					))}
				</tr>
			))}
		</>
	);
}

function TableHead({ headers }: TableHeadProps) {
	return (
		<thead>
			<tr className="border-b border-gray-200 text-gray-800">
				{headers.map((header, index) => (
					<th key={index} className="p-3 text-left">
						{header}
					</th>
				))}
			</tr>
		</thead>
	);
}

function AdvancedTableHead({ headers }: AdvancedTableHeadProps) {
	return (
		<thead>
			<tr className="border-b border-gray-200 text-gray-800">
				{headers.map((header, index) => {
					if (header.key) {
						return (
							<th key={index} className={`cursor-pointer p-3 text-left ${header?.hideOnMobile && 'hidden sm:table-cell'}`}>
								{header.name}
							</th>
						);
					}
					return (
						<th key={index} className={`p-3 text-left ${header?.hideOnMobile && 'hidden sm:table-cell'}`}>
							{header.name}
						</th>
					);
				})}
			</tr>
		</thead>
	);
}

function TableFrame({ headers, rows, className = '' }: { rows: ReactNode } & TableProps) {
	return (
		<table className={`border-collapse overflow-hidden rounded-md bg-white text-sm shadow-md ${className}`}>
			{headers && <TableHead headers={headers} />}
			<tbody>{rows && rows}</tbody>
		</table>
	);
}

function AdvancedTableFrame({ headers, rows, className = '' }: { rows: ReactNode } & AdvancedTableProps) {
	return (
		<table className={`border-collapse overflow-hidden rounded-md bg-white text-sm shadow-md ${className}`}>
			{headers && <AdvancedTableHead headers={headers} />}
			<tbody>{rows && rows}</tbody>
		</table>
	);
}

export function Table({ headers, rows, className = '' }: RowProps & TableProps) {
	return <TableFrame headers={headers} rows={<TableRows rows={rows} />} className={className} />;
}

export function LinkTable({ headers, rows, className = '' }: LinkRowProps & TableProps) {
	return <TableFrame headers={headers} rows={<LinkTableRows rows={rows} />} className={className} />;
}

export function AdvancedTable({ headers, rows, className = '' }: RowProps & AdvancedTableProps) {
	return <AdvancedTableFrame headers={headers} rows={<AdvancedTableRows rows={rows} headers={headers} />} className={className} />;
}
